import type { Relationship as RelationshipEnum } from "prisma-internal";

/**
 * PrismaのRelationship型から"relationship_"プレフィックスを削除する型
 */
type RemovePrefix<T, P extends string> = T extends `${P}${infer Rest}`
  ? Rest
  : T;
type RelationshipCode = RemovePrefix<RelationshipEnum, "relationship_">;

/**
 * 続柄コードと日本語表記のマッピング
 */
const relationshipKinds = {
  // 配偶者
  "11": "夫",
  "12": "妻",
  "13": "夫", // 内夫
  "14": "妻", // 内妻

  // 子（男女とも「子」に統一）
  "21": "子",
  "22": "子",
  "23": "子",
  "24": "子",
  "25": "子",
  "26": "子",
  "27": "子",
  "28": "子",
  "29": "子",
  "2A": "子",
  "31": "子",
  "32": "子",
  "33": "子",
  "34": "子",
  "35": "子",
  "36": "子",
  "37": "子",
  "38": "子",
  "39": "子",
  "3A": "子",

  // 孫
  "41": "孫",
  "42": "孫",
  "43": "孫配偶者",
  "44": "孫配偶者",

  // 父母
  "51": "父",
  "52": "母",
  "53": "養父",
  "54": "養母",
  "55": "義父",
  "56": "義母",

  // 祖父母
  "61": "祖父",
  "62": "祖母",
  "63": "曽祖父",
  "64": "曽祖母",
  "65": "義祖父",
  "66": "義祖母",
  "67": "養祖父",
  "68": "養祖母",

  // 兄弟姉妹
  "71": "兄",
  "72": "弟",
  "73": "義兄",
  "74": "義弟",
  "81": "姉",
  "82": "妹",
  "83": "義姉",
  "84": "義妹",

  // その他親族
  "91": "叔父",
  "92": "叔母",
  "93": "甥",
  "94": "姪",
  "95": "伯父",
  "96": "伯母",
  "97": "他親", // 他親（男）
  "98": "他親", // 他親（女）
  "9A": "不明",
} as const satisfies Record<RelationshipCode, string>;

/**
 * マッピングの値の型を抽出
 */
export type RelationshipKind =
  (typeof relationshipKinds)[keyof typeof relationshipKinds];

/**
 * 続柄コード（rawな値）を日本語表記に変換する
 *
 * @param code - 続柄コード（例: "11", "21"）
 * @returns 続柄（例: "夫", "子"）。マッピングに存在しない場合はundefined
 */
function getRelationshipKind(
  code: RelationshipCode,
): RelationshipKind | undefined {
  return relationshipKinds[code];
}

// Prismaのrelationshipフィールドは、"relationship_"で始まる文字列が格納されているため、これを取り除く
const trimPrismaPrefix = (relationship: string) => {
  return relationship.replace(/^relationship_/, "") as RelationshipCode;
};

/**
 * 続柄のEnumをRelationshipKindに変換する
 * マッピングに存在しない場合はnullを返す
 */
export const convertRelationship = (
  relationship: string,
): RelationshipKind | null => {
  return getRelationshipKind(trimPrismaPrefix(relationship)) ?? null;
};

/**
 * Prismaの続柄フィールドを日本語表記の表示名に変換する
 *
 * @param relationship - Prismaの続柄フィールド（例: "relationship_21", null）
 * @returns 続柄（例: "子", "本人"）
 */
export function formatRelationshipToDisplay(
  relationship: string | null,
): RelationshipKind | "本人" | "不明" {
  if (relationship == null) {
    return "本人";
  }

  return convertRelationship(relationship) ?? "不明";
}
